import { Fragment, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, fmt, fmtDate, getUser, CUSTOMS_STATUS, stBadge } from '../api';
import { Field, Input, Modal, useToast, Loading, Eyebrow, ErrorBox } from '../ui';

/** Kargo/sklad ko'rinishi — narxlarsiz: nima kelyapti, guruh, kuba/kg. */
function KargoView({ id, nav }: { id: string; nav: any }) {
  const [d, setD] = useState<any>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  useEffect(() => { api.get(`/customs/${id}/logistics`).then(setD).catch(() => setD({ error: true })); }, [id]);
  if (!d) return <Loading />;
  if (d.error) return <div className="empty">Hujjatni ochib bo'lmadi</div>;
  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">{d.number}</h1>
          <p className="page-sub">
            <span className={'badge ' + stBadge(CUSTOMS_STATUS, d.status).cls}>{stBadge(CUSTOMS_STATUS, d.status).label}</span>
            {' '}· {fmtDate(d.date)} · {d.orders.map((o: any) => o.number).join(', ')}
          </p>
        </div>
        <button className="btn" onClick={() => nav('/customs')}>← Ro'yxat</button>
      </div>
      <Eyebrow title="Partiya yig'indisi" desc="kelayotgan yuk — kg va kub bilan" />
      <div className="stat-grid" style={{ marginBottom: 6 }}>
        <div className="stat accent"><div className="k">Jami dona</div><div className="v">{fmt(d.totals.totalUnits)}</div></div>
        <div className="stat"><div className="k">Jami mesta</div><div className="v">{fmt(d.totals.totalBoxes, 1)}</div></div>
        <div className="stat"><div className="k">Jami brutto kg</div><div className="v">{fmt(d.totals.totalGrossKg, 1)}</div></div>
        <div className="stat"><div className="k">Jami m³</div><div className="v">{fmt(d.totals.totalM3, 2)}</div></div>
      </div>
      <Eyebrow title="Yuk tarkibi" desc="guruh bo'yicha — qatorni ochib ichidagi mahsulotlarni ko'ring" />
      <div className="table-wrap">
        <table>
          <thead><tr>
            <th className="num">№</th><th>Rastamojka nomi</th><th>TNVED</th><th>Shakli</th>
            <th className="num">Dona</th><th className="num">Mesta</th><th className="num">Brutto kg</th>
            <th className="num">Netto kg</th><th className="num">m³</th>
          </tr></thead>
          <tbody>
            {d.rows.map((r: any, i: number) => (
              <Fragment key={i}>
                <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === i ? null : i)}>
                  <td className="num muted">{r.n ?? i + 1}</td>
                  <td><span className="tree-toggle">{expanded === i ? '▾' : '▸'}</span>{r.group}</td>
                  <td>{r.tnved}</td>
                  <td><span className="badge b-confirmed">{r.form === 'piece' ? 'Donaga' : 'Kg'}</span></td>
                  <td className="num">{fmt(r.units)}</td>
                  <td className="num">{fmt(r.boxes, 1)}</td>
                  <td className="num">{fmt(r.grossKg, 1)}</td>
                  <td className="num">{r.netKg == null ? <span className="muted">—</span> : fmt(r.netKg, 2)}</td>
                  <td className="num">{fmt(r.volumeM3, 2)}</td>
                </tr>
                {/* Tarkib — qator-qator, bir qatorga tiqilgan yorliqlar emas */}
                {expanded === i && r.articles.map((a: any, k: number) => (
                  <tr key={k} className="sub-row">
                    <td className="num muted">{i + 1}.{k + 1}</td>
                    <td style={{ paddingLeft: 30 }}>
                      <span className="mono muted">{a.article}</span> · {a.name}
                      {a.power && <span className="badge b-draft" style={{ marginLeft: 6 }}>{a.power}</span>}
                    </td>
                    <td colSpan={2}></td>
                    <td className="num">{fmt(a.qty)}</td>
                    <td colSpan={4}></td>
                  </tr>
                ))}
                {expanded === i && !r.articles.length && (
                  <tr className="sub-row"><td></td><td colSpan={8} className="muted" style={{ paddingLeft: 30 }}>Tarkibi bo'sh</td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CustomsEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const user = getUser();
  const canEdit = ['admin', 'rastamojka'].includes(user?.role);
  const isKargo = user?.role === 'kargo';
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [freeOrders, setFreeOrders] = useState<any[]>([]);
  const [attaching, setAttaching] = useState<number[]>([]);
  // Tahrir huquqi yo'q rollar (rahbar/zakaz_operator) uchun ham maydonlar qulflanadi
  const locked = (d && d.status === 'confirmed') || !canEdit;

  const load = () => api.get('/customs/' + id).then(setD).catch((e) => { setErr(e.message); });
  // Ochilganda: tasdiqlanmagan hujjatni avtomatik qayta guruhlash — faqat tahrir huquqi borlar uchun
  const open = () => {
    setErr('');
    api.get('/customs/' + id).then((doc) => {
      if (canEdit && ['draft', 'calculated'].includes(doc.status)) {
        api.post(`/customs/${id}/regroup`).then(setD).catch(() => setD(doc));
      } else setD(doc);
    }).catch((e) => setErr(e.message));
  };
  useEffect(() => { if (!isKargo) open(); }, [id]);

  if (isKargo) return <KargoView id={id!} nav={nav} />;

  const saveHead = async () => {
    try { await api.patch('/customs/' + id, { gtdNumber: d.gtdNumber, usdRate: d.usdRate, cargoCostUsd: d.cargoCostUsd, totalFeeUsd: d.totalFeeUsd, note: d.note }); toast('Saqlandi'); load(); }
    catch (e: any) { toast(e.message, true); }
  };
  const regroup = async () => { try { await api.post(`/customs/${id}/regroup`); toast('Qayta guruhlandi'); load(); } catch (e: any) { toast(e.message, true); } };
  const saveItem = async (item: any) => {
    try { await api.patch(`/customs/${id}/items/${item.id}`, { netKg: item.netKg, basePrice: item.basePrice, invoicePrice: item.invoicePrice, currency: item.currency, salePriceUsd: item.salePriceUsd, prixodPriceUzs: item.prixodPriceUzs, tnved: item.tnved }); toast('Qator saqlandi'); load(); }
    catch (e: any) { toast(e.message, true); }
  };
  const doConfirm = async () => {
    if (!window.confirm('Tasdiqlansinmi? Invoys narxlar tarixga yoziladi va hujjat qulflanadi.')) return;
    try { await api.post(`/customs/${id}/confirm`); toast('Tasdiqlandi ✅'); load(); }
    catch (e: any) {
      // «invoys narx yo'q» ogohlantirishi — baribir tasdiqlash imkoni bilan
      if (String(e.message).includes('invoys narx')) {
        if (!window.confirm(e.message + '\n\nBaribir tasdiqlansinmi?')) return;
        try { await api.post(`/customs/${id}/confirm`, { force: true }); toast('Tasdiqlandi ✅'); load(); }
        catch (e2: any) { toast(e2.message, true); }
        return;
      }
      toast(e.message, true);
    }
  };
  const unlock = async () => { try { await api.post(`/customs/${id}/unlock`); toast('Qulf ochildi'); load(); } catch (e: any) { toast(e.message, true); } };

  // ── Zakazlarni ulash / uzish ──
  const openAttach = async () => {
    setAttaching([]);
    try {
      const [received, partial, transit, decls] = await Promise.all([
        api.get('/orders?status=received'), api.get('/orders?status=partial'),
        api.get('/orders?status=in_transit'), api.get('/customs'),
      ]);
      const taken = new Set<number>();
      decls.forEach((x: any) => { if (x.status !== 'archive') x.orders.forEach((o: any) => taken.add(o.orderId ?? o.order?.id)); });
      setFreeOrders([...received, ...partial, ...transit].filter((o: any) => !taken.has(o.id)));
      setAttachOpen(true);
    } catch (e: any) { toast(e.message, true); }
  };
  const doAttach = async () => {
    try { await api.post(`/customs/${id}/attach-orders`, { orderIds: attaching }); toast('Ulandi va qayta guruhlandi'); setAttachOpen(false); load(); }
    catch (e: any) { toast(e.message, true); }
  };
  const doDetach = async (orderId: number, number: string) => {
    if (!confirm(`${number} hujjatdan uzilsinmi? Qatorlar qayta guruhlanadi.`)) return;
    try { await api.del(`/customs/${id}/orders/${orderId}`); toast('Uzildi'); load(); }
    catch (e: any) { toast(e.message, true); }
  };

  const updItem = (itemId: number, patch: any) => setD({ ...d, items: d.items.map((it: any) => it.id === itemId ? { ...it, ...patch } : it) });

  if (err && !d) return <ErrorBox text={err} onRetry={open} />;
  if (!d) return <Loading />;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">{d.number}</h1>
          <p className="page-sub"><span className={'badge ' + stBadge(CUSTOMS_STATUS, d.status).cls}>{stBadge(CUSTOMS_STATUS, d.status).label}</span> · {d.orders.map((o: any) => o.order.number).join(', ')}</p>
        </div>
        <div className="head-actions">
          <button className="btn" onClick={() => nav('/customs')}>← Ro'yxat</button>
          <button className="btn" title="Deklarantga beriladigan Excel jadval"
            onClick={() => api.download(`/customs/${id}/export`, `${d.number}.xlsx`)}>
            ⬇ Deklarant Excel
          </button>
          {canEdit && !locked && <button className="btn" onClick={openAttach}>+ Zakaz ulash</button>}
          {canEdit && !locked && <button className="btn" onClick={regroup}>↻ Qayta guruhlash</button>}
          {canEdit && !locked && <button className="btn btn-primary" onClick={doConfirm}>Tasdiqlash</button>}
          {user?.role === 'admin' && d.status === 'confirmed' && <button className="btn btn-danger" onClick={unlock}>🔓 Qulfdan chiqarish</button>}
        </div>
      </div>

      {d.ungrouped.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--warn)', marginBottom: 16 }}>
          ⚠ <b>{d.ungrouped.length} ta guruhsiz pozitsiya</b> — tasdiqlashdan oldin katalogda rastamojka guruhini biriktiring:
          <div style={{ marginTop: 8 }}>{d.ungrouped.map((u: any) => <span key={u.orderItemId} className="tag">{u.article} · {u.name}</span>)}</div>
        </div>
      )}

      <Eyebrow title="Ulangan zakazlar" desc="qaysi zakazlar shu partiyada" />
      <div className="card" style={{ marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {d.orders.length === 0 && <span className="muted">Zakaz ulanmagan — «+ Zakaz ulash» tugmasidan foydalaning</span>}
        {d.orders.map((o: any) => (
          <span key={o.orderId} className="tag" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span className="link" style={{ cursor: 'pointer' }} onClick={() => nav('/orders/' + o.orderId)}>{o.order.number}</span>
            <span className="muted">{o.order.supplier?.name || '—'}</span>
            {canEdit && !locked && (
              <button className="act act-del" title="Uzish" style={{ width: 20, height: 20 }}
                onClick={() => doDetach(o.orderId, o.order.number)}>✕</button>
            )}
          </span>
        ))}
      </div>

      <Eyebrow title="Hujjat ma'lumotlari" desc="GTD, kurs, kargo va yig'im" />
      <div className="card" style={{ marginBottom: 6 }}>
        <div className="grid-3">
          <Field label="GTD raqami"><Input disabled={locked} value={d.gtdNumber || ''} onChange={(e) => setD({ ...d, gtdNumber: e.target.value })} /></Field>
          <Field label="$ kursi"><Input type="number" step="0.0001" disabled={locked} value={d.usdRate ?? ''} onChange={(e) => setD({ ...d, usdRate: e.target.value })} /></Field>
          <Field label="Kargo xarajati ($)"><Input type="number" step="0.01" disabled={locked} value={d.cargoCostUsd ?? ''} onChange={(e) => setD({ ...d, cargoCostUsd: e.target.value })} /></Field>
          <Field label="Partiya yig'imi / юлкиро ($)"><Input type="number" step="0.01" disabled={locked} value={d.totalFeeUsd ?? ''} onChange={(e) => setD({ ...d, totalFeeUsd: e.target.value })} /></Field>
          <Field label="Izoh"><Input disabled={locked} value={d.note || ''} onChange={(e) => setD({ ...d, note: e.target.value })} /></Field>
        </div>
        {canEdit && !locked && <button className="btn btn-primary btn-sm" onClick={saveHead}>Sarlavhani saqlash</button>}
      </div>

      <Eyebrow title="Partiya yig'indisi" desc="ulangan zakazlar bo'yicha avtomatik" />
      <div className="stat-grid" style={{ marginBottom: 6 }}>
        <div className="stat accent"><div className="k">Jami dona</div><div className="v">{fmt(d.totals.totalUnits)}</div></div>
        <div className="stat"><div className="k">Jami mesta</div><div className="v">{fmt(d.totals.totalBoxes, 1)}</div></div>
        <div className="stat"><div className="k">Jami brutto kg</div><div className="v">{fmt(d.totals.totalGrossKg, 1)}</div></div>
        <div className="stat"><div className="k">Jami m³</div><div className="v">{fmt(d.totals.totalM3, 2)}</div></div>
      </div>

      <Eyebrow title="Rastamojka qatorlari" desc="guruh bo'yicha avtomatik yig'ilgan — qatorni ochib tarkibini ko'ring" />
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="num">№</th><th>Rastamojka nomi</th><th>TNVED</th><th>Shakli</th><th className="num">Dona</th><th className="num">Mesta</th>
              <th className="num">Brutto kg</th><th className="num">Netto kg</th><th className="num">m³</th><th className="num">Baza</th><th className="num">Invoys narx</th><th>Val.</th><th className="num">Sotuv narxi</th><th className="num">Sotuvdan 60%</th>
              <th className="num" title="Umumiy etalon: eng qimmat narx (so'm)">Prixod narxi<div className="th-sub">etalon · so'm</div></th>
              <th className="num" title="Aynan shu rastamojkadagi haqiqiy qiymat (so'm)">Стоимость приходда<div className="th-sub">shu rastamojkada</div></th>
              <th className="num" title="Prixod narxi × 1,1 × 1,12">Shartnoma bera olamiz<div className="th-sub">× 1,1 × 1,12</div></th><th></th>
            </tr>
          </thead>
          <tbody>
            {d.items.map((it: any, idx: number) => (
              <Fragment key={it.id}>
                <tr>
                  <td className="num muted">{idx + 1}</td>
                  <td style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === it.id ? null : it.id)}>
                    <span className="tree-toggle">{expanded === it.id ? '▾' : '▸'}</span>{it.customsGroup?.name}
                  </td>
                  <td><input className="input" style={{ width: 110 }} disabled={locked} value={it.tnved} onChange={(e) => updItem(it.id, { tnved: e.target.value })} /></td>
                  <td><span className="badge b-confirmed">{it.form === 'piece' ? 'Donaga' : 'Kg'}</span></td>
                  <td className="num">{fmt(it.qtyUnits)}</td><td className="num">{fmt(it.qtyBoxes, 1)}</td>
                  <td className="num">{fmt(it.grossKg, 1)}</td>
                  <td className="num"><input className="input num" style={{ width: 80 }} type="number" disabled={locked} value={it.netKg ?? ''} onChange={(e) => updItem(it.id, { netKg: e.target.value })} /></td>
                  <td className="num">{fmt(it.volumeM3, 2)}</td>
                  <td className="num"><input className="input num" style={{ width: 80 }} type="number" step="0.0001" disabled={locked} value={it.basePrice ?? ''} onChange={(e) => updItem(it.id, { basePrice: e.target.value })} /></td>
                  <td className="num"><input className="input num" style={{ width: 80 }} type="number" step="0.0001" disabled={locked} value={it.invoicePrice ?? ''} onChange={(e) => updItem(it.id, { invoicePrice: e.target.value })} /></td>
                  <td><input className="input" style={{ width: 60 }} disabled={locked} value={it.currency} onChange={(e) => updItem(it.id, { currency: e.target.value })} /></td>
                  <td className="num"><input className="input num" style={{ width: 80 }} type="number" step="0.01" disabled={locked} value={it.salePriceUsd ?? ''} onChange={(e) => updItem(it.id, { salePriceUsd: e.target.value })} /></td>
                  <td className="num muted">{it.salePriceUsd ? fmt(Number(it.salePriceUsd) * 0.6, 2) : '—'}</td>
                  <td className="num"><input className="input num" style={{ width: 100 }} type="number" step="0.01" disabled={locked} value={it.prixodPriceUzs ?? ''} onChange={(e) => updItem(it.id, { prixodPriceUzs: e.target.value })} /></td>
                  <td className="num"><input className="input num" style={{ width: 100 }} type="number" step="0.01" disabled={locked} value={it.prixodCostUzs ?? ''} onChange={(e) => updItem(it.id, { prixodCostUzs: e.target.value })} /></td>
                  <td className="num" style={{ fontWeight: 700 }} title="Prixod narxi × 1,1 (ustama) × 1,12 (NDS)">
                    {it.prixodPriceUzs ? fmt(Number(it.prixodPriceUzs) * 1.1 * 1.12, 0) : <span className="muted">—</span>}
                  </td>
                  <td>{canEdit && !locked && <button className="btn btn-sm btn-primary" onClick={() => saveItem(it)}>✓</button>}</td>
                </tr>
                {/* Tarkib — qator-qator. Bir guruhda 9W va 18W chiroq bo'lsa
                    narxlari har xil, shuning uchun har mahsulot o'z narxi bilan turadi. */}
                {expanded === it.id && it.lines.map((l: any, k: number) => (
                  <tr key={l.id} className="sub-row">
                    <td className="num muted">{idx + 1}.{k + 1}</td>
                    <td style={{ paddingLeft: 30 }}>
                      <span className="mono muted">{l.orderItem.product.article}</span> · {l.orderItem.product.name}
                      {l.orderItem.product.powerLabel && <span className="badge b-draft" style={{ marginLeft: 6 }}>{l.orderItem.product.powerLabel}</span>}
                    </td>
                    <td colSpan={2}></td>
                    <td className="num">{fmt(l.qty)}</td>
                    {/* Mesta · Brutto · Netto · m³ · Baza · Invoys · Val. — guruh darajasida */}
                    <td colSpan={7}></td>
                    <td className="num">{l.salePriceUsd == null ? <span className="muted">—</span> : '$' + fmt(l.salePriceUsd, 2)}</td>
                    <td className="num muted">{l.salePriceUsd == null ? '—' : fmt(Number(l.salePriceUsd) * 0.6, 2)}</td>
                    <td className="num" style={{ fontWeight: 600, opacity: l.prixodOwn ? 1 : 0.55 }}
                      title={l.prixodPriceUzs == null ? '' : l.prixodOwn ? "Shu mahsulotning o'z kirim narxi" : 'Guruh narxi — bu mahsulot hali alohida o\'lchanmagan'}>
                      {l.prixodPriceUzs == null ? <span className="muted">—</span> : fmt(l.prixodPriceUzs, 2)}
                      {l.prixodPriceUzs != null && !l.prixodOwn && <span className="muted" style={{ fontSize: 10, marginLeft: 4 }}>guruh</span>}
                    </td>
                    <td className="num" style={{ color: 'var(--ok)' }}>{l.prixodCostUzs == null ? <span className="muted">—</span> : fmt(l.prixodCostUzs, 2)}</td>
                    <td className="num" style={{ fontWeight: 600, opacity: l.prixodOwn ? 1 : 0.55 }}>{l.contractPrice == null ? <span className="muted">—</span> : fmt(l.contractPrice, 2)}</td>
                    <td></td>
                  </tr>
                ))}
                {expanded === it.id && !it.lines.length && (
                  <tr className="sub-row"><td></td><td colSpan={17} className="muted" style={{ paddingLeft: 30 }}>Tarkibi bo'sh</td></tr>
                )}
              </Fragment>
            ))}
            {d.items.length === 0 && <tr><td colSpan={18} className="empty">Guruhlangan qator yo'q — zakazlarni ulab, qayta guruhlang</td></tr>}
          </tbody>
        </table>
      </div>
      {attachOpen && (
        <Modal title="Hujjatga zakaz ulash" onClose={() => setAttachOpen(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {freeOrders.length === 0 && <span className="muted">Ulash uchun bo'sh zakaz yo'q</span>}
            {freeOrders.map((o) => (
              <label key={o.id} className="chip" style={{ cursor: 'pointer', borderColor: attaching.includes(o.id) ? 'var(--accent)' : undefined }}>
                <input type="checkbox" checked={attaching.includes(o.id)}
                  onChange={() => setAttaching(attaching.includes(o.id) ? attaching.filter((x) => x !== o.id) : [...attaching, o.id])} />
                {o.number} · {o.supplier?.name || '—'} · {o.positions} poz.
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn" onClick={() => setAttachOpen(false)}>Bekor</button>
            <button className="btn btn-primary" disabled={!attaching.length} onClick={doAttach}>Ulash va guruhlash</button>
          </div>
        </Modal>
      )}

      <p className="muted" style={{ marginTop: 10, fontSize: 12 }}>
        ℹ️ Bu jadval kargo va skladchilar uchun — nima kelayotgani, qaysi kategoriya, kuba/kg bilan. Tannarx bu yerda hisoblanmaydi.
      </p>
    </div>
  );
}
